import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  ReorderButtons,
} from "../../../components/FormField";
import { moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";

export function ContactSections() {

  // ── Contact Info Sidebar ──────────────────────────────────────
  const [sidebarActive, setSidebarActive] = useState(true);
  const [sidebarTitle, setSidebarTitle] = useState("");
  const [sidebarDetails, setSidebarDetails] = useState([
    { type: "", label: "", sub_label: "", icon: "" },
  ]);

  // ── Benefits Section ──────────────────────────────────────────
  const [benefitsActive, setBenefitsActive] = useState(true);
  const [benefitsTitle, setBenefitsTitle] = useState("");
  const [benefitsItems, setBenefitsItems] = useState([""]);

  return (
    <>
      {/* Contact Info Sidebar */}
      <CollapsibleSection
        title="Contact Info Sidebar"
        active={sidebarActive}
        onToggleActive={setSidebarActive}
      >
        <Field label="Title">
          <TextInput
            value={sidebarTitle}
            onChange={(e) => setSidebarTitle(e.target.value)}
            placeholder="e.g. Get in Touch"
          />
        </Field>
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Details
            </p>
            <button
              type="button"
              onClick={() =>
                setSidebarDetails((s) => [
                  ...s,
                  { type: "", label: "", sub_label: "", icon: "" },
                ])
              }
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{
                background: "hsl(var(--accent-muted))",
                color: "hsl(var(--accent))",
              }}
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          {sidebarDetails.map((detail, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border border-border space-y-3 mb-2"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Detail {i + 1}
                </p>
                <div className="flex items-center gap-2">
                  <ReorderButtons
                    index={i}
                    total={sidebarDetails.length}
                    onMove={(f, t) =>
                      setSidebarDetails(moveItem(sidebarDetails, f, t))
                    }
                  />
                  {sidebarDetails.length > 1 && (
                    <button
                      onClick={() =>
                        setSidebarDetails((s) =>
                          s.filter((_, idx) => idx !== i),
                        )
                      }
                      className="text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <Field label="Type">
                <TextInput
                  value={detail.type}
                  onChange={(e) =>
                    setSidebarDetails((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, type: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="e.g. phone, email, address, hours"
                />
              </Field>
              <Field label="Icon">
                <select
                  value={detail.icon}
                  onChange={(e) =>
                    setSidebarDetails((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, icon: e.target.value } : x,
                      ),
                    )
                  }
                  className="w-full h-9 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/40"
                >
                  <option value="">— Select Icon —</option>
                  {ICON_OPTIONS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Label">
                <TextInput
                  value={detail.label}
                  onChange={(e) =>
                    setSidebarDetails((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, label: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="e.g. +61 3 7075 3900"
                />
              </Field>
              <Field label="Sub Label">
                <TextInput
                  value={detail.sub_label}
                  onChange={(e) =>
                    setSidebarDetails((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, sub_label: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="e.g. Call us during business hours"
                />
              </Field>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Benefits Section */}
      <CollapsibleSection
        title="Benefits Section"
        active={benefitsActive}
        onToggleActive={setBenefitsActive}
      >
        <Field label="Title">
          <TextInput
            value={benefitsTitle}
            onChange={(e) => setBenefitsTitle(e.target.value)}
            placeholder="e.g. Why Choose Us?"
          />
        </Field>
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Items
            </p>
            <button
              type="button"
              onClick={() => setBenefitsItems((s) => [...s, ""])}
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{
                background: "hsl(var(--accent-muted))",
                color: "hsl(var(--accent))",
              }}
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          {benefitsItems.map((item, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <TextInput
                value={item}
                onChange={(e) =>
                  setBenefitsItems((s) =>
                    s.map((x, idx) => (idx === i ? e.target.value : x)),
                  )
                }
                placeholder={`e.g. SAP Silver Partner`}
                className="flex-1"
              />
              <ReorderButtons
                index={i}
                total={benefitsItems.length}
                onMove={(f, t) =>
                  setBenefitsItems(moveItem(benefitsItems, f, t))
                }
              />
              {benefitsItems.length > 1 && (
                <button
                  onClick={() =>
                    setBenefitsItems((s) => s.filter((_, idx) => idx !== i))
                  }
                  className="text-destructive"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </CollapsibleSection>
    </>
  );
}
